import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessServiceContext = createContext();

export const useBusinessService = () => {
  const context = useContext(BusinessServiceContext);
  if (!context) {
    throw new Error('useBusinessService must be used within a BusinessServiceProvider');
  }
  return context;
};

export const BusinessServiceProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business service endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getServiceBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-services/api/v1/business-services`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-services/api/v1/business-services`;
    } else {
      throw new Error('Invalid login type for business services access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business services');
    }
  }, [loginType]);

  // Get services by registry number with pagination and filters
  const getServicesByRegistryNumber = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.serviceCategory) queryParams.append('service_category', options.serviceCategory);
      if (options.servicePriceType) queryParams.append('service_price_type', options.servicePriceType);
      if (options.serviceVisibility !== undefined) queryParams.append('service_visibility', options.serviceVisibility);
      if (options.minPrice) queryParams.append('min_price', options.minPrice);
      if (options.maxPrice) queryParams.append('max_price', options.maxPrice);
      if (options.minCapacity) queryParams.append('min_capacity', options.minCapacity);
      if (options.maxCapacity) queryParams.append('max_capacity', options.maxCapacity);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getServiceBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch services';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

  // Get service statistics by registry number
  const getServiceStats = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getServiceBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get service statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

  // Get service categories by registry number
  const getServiceCategories = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getServiceBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/categories`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get service categories';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

  // Get service price types by registry number
  const getServicePriceTypes = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getServiceBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/price-types`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get service price types';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

  // Get service by service ID
  const getServiceById = useCallback(async (serviceId) => {
    validateAccess();
    
    if (!serviceId) {
      throw new Error('Service ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getServiceBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/service/${encodeURIComponent(serviceId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get service by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

  // Get services filtered by category
  const getServicesByCategory = useCallback(async (registryNumber, category, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      serviceCategory: category,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get services filtered by price type
  const getServicesByPriceType = useCallback(async (registryNumber, priceType, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      servicePriceType: priceType,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get services filtered by visibility
  const getServicesByVisibility = useCallback(async (registryNumber, isVisible, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      serviceVisibility: isVisible,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get services by price range
  const getServicesByPriceRange = useCallback(async (registryNumber, minPrice, maxPrice, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      minPrice: minPrice,
      maxPrice: maxPrice,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get services by capacity range
  const getServicesByCapacityRange = useCallback(async (registryNumber, minCapacity, maxCapacity, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      minCapacity: minCapacity,
      maxCapacity: maxCapacity,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Search services
  const searchServices = useCallback(async (registryNumber, searchTerm, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      search: searchTerm,
    };

    return await getServicesByRegistryNumber(registryNumber, filterOptions);
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get visible services only
  const getVisibleServices = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getServicesByVisibility(registryNumber, true, options);
  }, [getServicesByVisibility, validateAccess]);

  // Get hourly services
  const getHourlyServices = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getServicesByPriceType(registryNumber, 'hourly', options);
  }, [getServicesByPriceType, validateAccess]);

  // Get fixed price services
  const getFixedPriceServices = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getServicesByPriceType(registryNumber, 'fixed', options);
  }, [getServicesByPriceType, validateAccess]);

  // Export services data (prepare data for CSV/Excel export)
  const exportServicesData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all services without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getServicesByRegistryNumber(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.services.map(service => ({
      'Service ID': service.service_id,
      'Service Name': service.service_name,
      'Description': service.service_description,
      'Category': service.service_category,
      'Price Type': service.service_price_type,
      'Min Price': service.service_min_price ? `$${service.service_min_price.toFixed(2)}` : 'N/A',
      'Max Price': service.service_max_price ? `$${service.service_max_price.toFixed(2)}` : 'N/A',
      'Cost': `$${service.service_cost.toFixed(2)}`,
      'Capacity': service.service_capacity,
      'Duration': service.service_duration || 'N/A',
      'Visible': service.service_visibility ? 'Yes' : 'No',
      'Image Count': service.service_image_count || 0,
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `services_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getServicesByRegistryNumber, validateAccess]);

  // Health check for business services service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-services/health`
        : `${API_BASE_URL}/admin/business-services/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateServiceFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.minPrice && filters.minPrice < 0) {
      errors.push({ field: 'minPrice', message: 'Minimum price must be non-negative' });
    }

    if (filters.maxPrice && filters.maxPrice < 0) {
      errors.push({ field: 'maxPrice', message: 'Maximum price must be non-negative' });
    }

    if (filters.minPrice && filters.maxPrice && filters.minPrice > filters.maxPrice) {
      errors.push({ field: 'priceRange', message: 'Minimum price must be less than maximum price' });
    }

    if (filters.minCapacity && filters.minCapacity < 0) {
      errors.push({ field: 'minCapacity', message: 'Minimum capacity must be non-negative' });
    }

    if (filters.maxCapacity && filters.maxCapacity < 0) {
      errors.push({ field: 'maxCapacity', message: 'Maximum capacity must be non-negative' });
    }

    if (filters.minCapacity && filters.maxCapacity && filters.minCapacity > filters.maxCapacity) {
      errors.push({ field: 'capacityRange', message: 'Minimum capacity must be less than maximum capacity' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Format service data for display
  const formatServiceData = useCallback((service) => {
    const getPriceDisplay = () => {
      if (service.service_min_price && service.service_max_price) {
        return `$${service.service_min_price.toFixed(2)} - $${service.service_max_price.toFixed(2)}`;
      } else if (service.service_min_price) {
        return `From $${service.service_min_price.toFixed(2)}`;
      } else if (service.service_max_price) {
        return `Up to $${service.service_max_price.toFixed(2)}`;
      } else {
        return 'Price on request';
      }
    };

    const formatDuration = (duration) => {
      if (!duration) return 'N/A';
      // Convert PostgreSQL interval to readable format
      // Assuming duration comes as "HH:MM:SS" format
      const parts = duration.split(':');
      if (parts.length === 3) {
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        if (hours > 0) {
          return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        } else if (minutes > 0) {
          return `${minutes}m`;
        }
      }
      return duration;
    };

    return {
      ...service,
      priceDisplay: getPriceDisplay(),
      formattedCost: `$${service.service_cost.toFixed(2)}`,
      priceTypeColor: service.service_price_type === 'fixed' ? 'green' :
                     service.service_price_type === 'hourly' ? 'blue' :
                     service.service_price_type === 'per_person' ? 'purple' : 'orange',
      visibilityText: service.service_visibility ? 'Visible' : 'Hidden',
      visibilityColor: service.service_visibility ? 'green' : 'red',
      capacityText: `${service.service_capacity} ${service.service_capacity === 1 ? 'person' : 'people'}`,
      durationFormatted: formatDuration(service.service_duration),
      hasImages: service.service_image_count > 0,
      imageCountText: service.service_image_count 
        ? `${service.service_image_count} image${service.service_image_count > 1 ? 's' : ''}`
        : 'No images',
      shortDescription: service.service_description && service.service_description.length > 100 
        ? service.service_description.substring(0, 100) + '...'
        : service.service_description,
      profitMargin: service.service_min_price 
        ? ((service.service_min_price - service.service_cost) / service.service_min_price * 100).toFixed(1) + '%'
        : 'N/A',
    };
  }, []);

  // Get service summary for a business
  const getServiceSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, services] = await Promise.all([
        getServiceStats(registryNumber),
        getServicesByRegistryNumber(registryNumber, { limit: 10 }) // Get sample services
      ]);

      return {
        registryNumber,
        totalServices: stats.stats.total_services,
        visibleServices: stats.stats.visible_services,
        hiddenServices: stats.stats.hidden_services,
        averageMinPrice: stats.stats.average_min_price,
        averageMaxPrice: stats.stats.average_max_price,
        averageCost: stats.stats.average_cost,
        totalCapacity: stats.stats.total_capacity,
        servicesByCategory: stats.stats.services_by_category,
        servicesByPriceType: stats.stats.services_by_price_type,
        sampleServices: services.services,
        accessLevel: loginType,
        hasServices: stats.stats.total_services > 0,
        visibilityRate: stats.stats.total_services > 0 
          ? (stats.stats.visible_services / stats.stats.total_services * 100).toFixed(1) + '%'
          : '0%',
        averageProfitMargin: stats.stats.average_min_price > 0 && stats.stats.average_cost > 0
          ? ((stats.stats.average_min_price - stats.stats.average_cost) / stats.stats.average_min_price * 100).toFixed(1) + '%'
          : 'N/A',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get service summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getServiceStats, getServicesByRegistryNumber, validateAccess, loginType]);

  // Get service pricing analysis
  const getServicePricingAnalysis = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getServicesByRegistryNumber(registryNumber, { limit: 1000 });
      const services = result.services;
      
      if (services.length === 0) {
        return {
          registryNumber,
          count: 0,
          averageMinPrice: 0,
          averageMaxPrice: 0,
          averageCost: 0,
          lowestPrice: 0,
          highestPrice: 0,
          priceDistribution: {},
        };
      }

      const minPrices = services.filter(s => s.service_min_price).map(s => s.service_min_price);
      const maxPrices = services.filter(s => s.service_max_price).map(s => s.service_max_price);
      const costs = services.map(s => s.service_cost);

      const allPrices = [...minPrices, ...maxPrices].filter(p => p > 0);
      
      return {
        registryNumber,
        count: services.length,
        averageMinPrice: minPrices.length > 0 ? minPrices.reduce((a, b) => a + b, 0) / minPrices.length : 0,
        averageMaxPrice: maxPrices.length > 0 ? maxPrices.reduce((a, b) => a + b, 0) / maxPrices.length : 0,
        averageCost: costs.reduce((a, b) => a + b, 0) / costs.length,
        lowestPrice: allPrices.length > 0 ? Math.min(...allPrices) : 0,
        highestPrice: allPrices.length > 0 ? Math.max(...allPrices) : 0,
        priceDistribution: {
          under25: allPrices.filter(p => p < 25).length,
          from25to100: allPrices.filter(p => p >= 25 && p < 100).length,
          from100to500: allPrices.filter(p => p >= 100 && p < 500).length,
          over500: allPrices.filter(p => p >= 500).length,
        }
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get pricing analysis';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getServicesByRegistryNumber, validateAccess]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-services/api/v1/business-services`
        : `${API_BASE_URL}/admin/business-services/api/v1/business-services`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getServicesByRegistryNumber,
    getServiceStats,
    getServiceCategories,
    getServicePriceTypes,
    getServiceById,
    
    // Search and Filter Actions
    getServicesByCategory,
    getServicesByPriceType,
    getServicesByVisibility,
    getServicesByPriceRange,
    getServicesByCapacityRange,
    searchServices,
    getVisibleServices,
    getHourlyServices,
    getFixedPriceServices,
    
    // Utility Actions
    exportServicesData,
    checkHealth,
    getServiceSummary,
    getServicePricingAnalysis,
    
    // Helpers
    clearError,
    validateServiceFilters,
    formatServiceData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasServicesAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessServiceContext.Provider value={value}>
      {children}
    </BusinessServiceContext.Provider>
  );
};